import { Controller } from '@hotwired/stimulus';

import Swiper from 'swiper';
import { Keyboard, Mousewheel, Navigation, Pagination } from 'swiper/modules';

export default class LightboxController extends Controller {
    static targets = [
        'image',
        'lightboxTemplate',
        'imageTemplate',
    ];

    static values = {
        id:               String,
        animationTimeout: { type: Number, default: 200 },
        swiperOptions:    { type: Object, default: {} },
    };

    static classes = [
        'hidden', 'fade', 'animateShow', 'animateHide',
    ];

    /** @var {Swiper|null} */
    _swiper = null;
    _swiperOptions = {};

    /** @var {HTMLElement|null} */
    _lightbox = null;

    /** @var {Array<HTMLElement>} */
    _slides = [];

    _isShown = false;


    get id() {
        return this.idValue;
    }

    /**
     * @returns {Array<HTMLAnchorElement|HTMLElement>}
     */
    get images() {
        return this.imageTargets;
    }

    /**
     * @returns {HTMLTemplateElement}
     */
    get lightboxTemplate() {
        return this.lightboxTemplateTarget;
    }

    /**
     * @returns {HTMLTemplateElement}
     */
    get imageTemplate() {
        return this.imageTemplateTarget;
    }

    initialize() {
        if (window.location.hash === `#lightbox-${this.id}`) {
            this._show(null, true);
        }
    }

    connect() {
        window.addEventListener('keyup', this._handleKeys);
        window.addEventListener('popstate', this._handleHistory);
    }

    disconnect() {
        window.removeEventListener('keyup', this._handleKeys);
        window.removeEventListener('popstate', this._handleHistory);

        this._swiper?.destroy();
        this._swiper = null;

        this._lightbox?.remove();
        this._lightbox = null;
    }

    /**
     * @param {Event} event
     * @returns {boolean}
     */
    show(event) {
        event.preventDefault();

        const src = event.currentTarget.href || event.currentTarget.dataset.src;
        this._show(src);

        return false;
    }

    /**
     * @param {String|null} [source=null]
     * @param {Boolean} restore
     * @private
     */
    _show(source = null, restore = false) {
        if (this._isShown) {
            return;
        }

        if (!restore) {
            history.pushState({ lightbox: this.id }, '', `#lightbox-${this.id}`);
        }

        this._isShown = true;
        this._buildLightbox();

        this._lightbox.classList.remove(...this.hiddenClasses);

        // timeout needed to have removal of "hidden" apply and then animate opacity
        const lightbox = this._lightbox;
        setTimeout(() => lightbox.classList.remove(...this.fadeClasses), 1);

        if (source) {
            this._showSlide(source);
        }
    }

    /**
     * @param {Event} event
     * @param {Boolean} restore
     * @private
     */
    _hide = (event, restore = false) => {
        if (!this._isShown || !this._lightbox) {
            return;
        }

        if (event.target && event.target.closest && event.target.closest('.swiper-ui')) {
            // ignore events on swiper elements
            return;
        }

        if (!restore) {
            history.back();
        }

        this._isShown = false;

        const lightbox = this._lightbox;
        setTimeout(() => {
            lightbox.classList.add(...this.fadeClasses);
            setTimeout(() => lightbox.classList.add(...this.hiddenClasses), this.animationTimeoutValue);
        }, this.animationTimeoutValue);
    };

    /**
     * @param {KeyboardEvent} event
     */
    _handleKeys = (event) => {
        if (event.key === 'Escape') {
            this._hide(event);
        }
    };

    /**
     * @param {PopStateEvent} event
     */
    _handleHistory = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.state?.lightbox) {
            if (event.state.lightbox === this.id) {
                this._show(null, true);
            }
        } else {
            this._hide(event, true);
        }
    };

    /**
     * @private
     */
    _buildLightbox() {
        if (this._lightbox) {
            return;
        }

        const lightbox  = this.lightboxTemplate.content.cloneNode(true).firstElementChild;
        const container = lightbox.querySelector('[data-lightbox-slides]');

        lightbox.setAttribute('data-lightbox', 'true');

        const slides = {};

        let index = 0;
        for (const source of this.images) {
            const slide = this._buildSlide(source);
            slide.dataset.lightboxIndex = index++ + '';

            if (!(slide.dataset.src in slides)) {
                slides[slide.dataset.src] = true;
                this._slides.push(slide);
                container.append(slide);
            }
        }

        lightbox.querySelectorAll('[data-lightbox-close]').forEach((el) => el.addEventListener('click', this._hide));

        document.body.append(lightbox);
        this._lightbox = lightbox;

        this._buildSwiper();
    }

    _buildSwiper() {
        const swiperTarget = this._lightbox.querySelector('.swiper');

        if (!swiperTarget) {
            return;
        }

        if (this._slides.length < 2) {
            swiperTarget.querySelectorAll('.swiper-loading, [data-lightbox-prev], [data-lightbox-next]').forEach((el) => el.remove());

            return;
        }

        this._swiperOptions = {
            modules: [ Keyboard, Mousewheel, Navigation, Pagination ],

            lazyPreloaderClass: 'swiper-loading',

            centeredSlides: true,
            slidesPerView:  1,
            spaceBetween:   0,

            mousewheel: {
                enabled: true,
            },

            keyboard: {
                enabled: true,
            },

            navigation: {
                nextEl: '[data-lightbox-next]',
                prevEl: '[data-lightbox-prev]',
            },

            pagination: {
                el:                 '.swiper-pagination',
                clickable:          true,
                dynamicBullets:     true,
                dynamicMainBullets: 10,
            },

            ...this.swiperOptionsValue,
        };

        this._swiper = new Swiper(swiperTarget, this._swiperOptions);
    }

    /**
     * @param {HTMLAnchorElement|HTMLElement} source
     * @private
     */
    _buildSlide(source) {
        const slide = this.imageTemplate.content.cloneNode(true).firstElementChild;

        const img   = slide.querySelector('img');
        img.loading = 'lazy';

        const width  = parseInt(source.dataset.width);
        const height = parseInt(source.dataset.height);

        if (width > 0) {
            img.width = width;
        }

        if (height > 0) {
            img.height = height;
        }

        if (source.href) {
            img.src = source.href;
        } else {
            img.src = source.dataset.src;
        }

        slide.dataset.src = img.src;

        const name = slide.querySelector('[data-lightbox-name]');
        if (name) {
            if (source.dataset.name) {
                name.setAttribute('data-lightbox-text', 'true');
                name.append(document.createTextNode(source.dataset.name));
            } else {
                name.remove();
            }
        }

        const description = slide.querySelector('[data-lightbox-description]');
        if (description) {
            if (source.dataset.description) {
                description.setAttribute('data-lightbox-text', 'true');
                description.append(document.createTextNode(source.dataset.description));
            } else {
                description.remove();
            }
        }

        const copyright = slide.querySelector('[data-lightbox-copyright]');
        if (copyright) {
            if (source.dataset.copyright) {
                copyright.setAttribute('data-lightbox-text', 'true');
                copyright.append(document.createTextNode(source.dataset.copyright));
            } else {
                copyright.remove();
            }
        }


        return slide;
    }

    /**
     * @param {String} source
     * @private
     */
    _showSlide(source) {
        if (!this._swiper) {
            return;
        }

        for (const slide of this._slides) {
            if (slide.dataset.src === source) {
                const index = slide.dataset.lightboxIndex ? parseInt(slide.dataset.lightboxIndex) : null;

                if (index >= 0) {
                    if (this._swiperOptions.loop) {
                        this._swiper.slideToLoop(index, 0);
                    } else {
                        this._swiper.slideTo(index, 0);
                    }

                    break;
                }
            }
        }
    }
}
